import { AxiosInstance } from 'axios'

import {
  CloudPaymentsPayment,
  FortuneCookie,
  PaySelectionPayment,
  SberbankPayment,
  SbpPaySelectionPayment,
  SbpSberbankPayment,
  TipLinks,
} from '@/store/orders/order.types'

import {
  HttpStatusCode,
  ServerError,
  Success,
  UnauthorizedUserError,
  UnprocessableEntityError,
} from '@/api/results'
import {
  OrderReviewSuccessResponse,
  RejectOrderInfo,
} from '@/types/api/api-v3/orders'
import { Order } from '@/types/api/api-v3/orders/order'
import { SetRatingPayload } from '@/types/api/api-v4/order.js'

export function createOrderModule({
  axios,
  sentry,
  city,
}: {
  axios: AxiosInstance
  sentry: any
  city: string
}) {
  const baseUrlV4 = getApiBaseUrl(city, 4, {
    withoutUuid: true,
    withoutCity: true,
  })

  const baseUrlV5 = getApiBaseUrl(city, 5)

  return {
    async rejectOrder(orderId: string) {
      try {
        await axios.post<RejectOrderInfo>(`/${city}/basket/order-reject/`, {
          order_id: orderId,
        })
      } catch (error) {
        // @ts-ignore
        switch (error?.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },
    async fetchFeedbackMethods() {
      try {
        const { data } = await axios.get(`/${city}/feedback_methods/`)
        return new Success(data)
      } catch (error) {
        // @ts-ignore
        switch (error?.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async fetchOrder(orderId: string) {
      try {
        const { data } = await axios.get<Order>(`/${city}/orders/${orderId}/`)

        return new Success(data)
      } catch (error) {
        switch (error?.response?.status) {
          default:
            sentry.captureException(error)
            return error
        }
      }
    },

    async paySavedCardPaySelection(orderId: string, cardId: string) {
      try {
        const { data } = await axios.post(
          `/${city}/online_payments/pay_selection_rebill_payment/`,
          {
            order_id: orderId,
            card_id: cardId,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async addReview(orderId: string, bodyRequest: FormData) {
      try {
        const { data } = await axios.post<OrderReviewSuccessResponse>(
          `/${city}/orders/${orderId}/quality_review/`,
          bodyRequest,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        )
        return new Success(data)
      } catch (error) {
        // @ts-ignore
        switch (error?.response?.status) {
          default:
            sentry.captureException(error)
            return error?.response
        }
      }
    },

    async fetchCloudPayment(orderId: string) {
      try {
        const { data } = await axios.post<CloudPaymentsPayment>(
          `/${city}/online_payments/cloud_payments_create_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        // @ts-ignore
        switch (error?.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async fetchCreatePayment(orderId: string) {
      try {
        const { data } = await axios.post<PaySelectionPayment>(
          `/${city}/online_payments/pay_selection_create_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async fetchSberbankPayment(orderId: string) {
      try {
        const { data } = await axios.post<SberbankPayment>(
          `/${city}/online_payments/sberbank_create_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async checkSberbankPayment(orderId: string) {
      try {
        const { data } = await axios.post<SberbankPayment>(
          `/${city}/online_payments/sberbank_check_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async fetchQrSberbank(orderId: string) {
      try {
        const { data } = await axios.post<SbpSberbankPayment>(
          `/${city}/online_payments/sbp_sberbank_create_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.FORBIDDEN:
            return new UnprocessableEntityError({
              message: 'Вы не являетесь владельцем заказа',
              data: error.response.data,
            })
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Платеж уже создан',
              data: error.response.data,
            })
          case HttpStatusCode.NOT_FOUND:
            return new UnprocessableEntityError({
              message: 'Заказ не найден',
              data: error.response.data,
            })
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({
              message: 'Учетные данные не были предоставлены',
            })
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так. Попробуйте позже')
        }
      }
    },

    async fetchCreateSbpTransaction(orderId: string) {
      try {
        const { data } = await axios.post<{ transaction_id: string }>(
          `${baseUrlV5}/online_payments/pay_selection_sbp_create_payment/`,
          {
            order_id: orderId,
          },
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(`Не удалось получить transaction_id: ${error}`)

        switch (error.response?.status) {
          case HttpStatusCode.FORBIDDEN:
            return new UnprocessableEntityError({
              message: 'Вы не являетесь владельцем заказа',
              data: error.response.data,
            })
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message:
                error.response.data?.detail || 'Не получилось создать платеж',
              data: error.response.data,
            })
          case HttpStatusCode.NOT_FOUND:
            return new UnprocessableEntityError({
              message:
                error.response.data?.detail || 'Не получилось создать платеж',
              data: error.response.data,
            })
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({
              message: 'Учетные данные не были предоставлены',
            })
          default:
            return new ServerError('Что-то пошло не так. Попробуйте позже')
        }
      }
    },

    async fetchQrPaySelection(orderId: string, transactionId: string) {
      try {
        const { data } = await axios.get<SbpPaySelectionPayment>(
          `${baseUrlV5}/online_payments/pay_selection_sbp/qr_code/order/${orderId}/transaction/${transactionId}/`,
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(`Не удалось получить sbp qr_code: ${error}`)

        switch (error.response?.status) {
          case HttpStatusCode.FORBIDDEN:
            return new UnprocessableEntityError({
              message:
                error.response.data?.detail ||
                'Вы не являетесь владельцем заказа',
              data: error.response.data,
            })
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: error.response.data?.detail || 'Платеж уже создан',
              data: error.response.data,
            })
          case HttpStatusCode.NOT_FOUND:
            return new UnprocessableEntityError({
              message: error.response.data?.detail || 'Заказ не найден',
              data: error.response.data,
            })
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({
              message: 'Учетные данные не были предоставлены',
            })
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так. Попробуйте позже')
        }
      }
    },

    async fetchFortuneCookie(orderId: string) {
      try {
        const { data } = await axios.get<FortuneCookie>(
          `/${city}/orders/${orderId}/fortune_cookie/`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async fetchTipLinks(orderId: string) {
      try {
        const { data } = await axios.get<TipLinks>(
          `/${city}/orders/${orderId}/tip/`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new ServerError('Что-то пошло не так')
        }
      }
    },

    async setRatingProducts(payload: SetRatingPayload) {
      try {
        const { data } = await axios.post(
          `${baseUrlV4}/rating/set_rating/`,
          payload,
        )

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)

        return new Error('Не удалось оценить рейтинг блюд')
      }
    },
  }
}
